import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData } from '../../../apiService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import './ItemModal.css';

// Compact section header
const sectionStyle = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: '#1e3a8a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  mb: 0.5,
  mt: 0.75,
  pb: 0.15,
  borderBottom: '1px solid #e5e7eb'
};

// Compact field styling
const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    '& input, & .MuiSelect-select': { padding: '6px 10px', fontSize: '0.75rem' },
    minHeight: '32px'
  },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.7rem' },
  '& .MuiAutocomplete-input': { padding: '6px 10px !important', fontSize: '0.75rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem', marginTop: '3px' },
  '& .MuiSvgIcon-root': { fontSize: '1.25rem' }
};

// Dropdown list styling
const autocompleteListStyle = {
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': {
      fontSize: '0.75rem',
      minHeight: '32px',
      padding: '4px 10px'
    }
  },
  '& .MuiAutocomplete-popper': {
    '& .MuiPaper-root': {
      fontSize: '0.75rem'
    }
  }
};

const MasterModal = ({
  open,
  onClose,
  modalType,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  loading
}) => {
  const { companyId, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [errors, setErrors] = useState({});
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [ledgerGroupLoading, setLedgerGroupLoading] = useState(false);
  const [ledgerGroupInputValue, setLedgerGroupInputValue] = useState('');
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  useEffect(() => {
    if (open && companyId && token) {
      fetchLedgerGroups();
      fetchBalanceSheetHeads();
      fetchCountries();
      fetchCurrencies();
    }
  }, [open, companyId, token]);

  // Sync form data when modal opens for editing
  useEffect(() => {
    if (open && modalAction === 'edit' && formData && Object.keys(formData).length > 0) {
      // Update ledger group input value
      if (formData.LedgerGroupName) {
        setLedgerGroupInputValue(formData.LedgerGroupName);
      }
    }
  }, [open, modalAction, formData.LedgerGroupName]);

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      // Set the input value when editing/viewing
      if (formData.LedgerGroupName) {
        setLedgerGroupInputValue(formData.LedgerGroupName);
      }
    } else if (modalAction === 'create') {
      const defaultIsType = modalType === 'client' ? 1 : modalType === 'vendor' ? 2 : 1;
      setFormData({
        Name: '', SearchableName: '', BalanceSheetHeadId: null,
        OpeningBalance: 0, OpeningDate: new Date().toISOString().split('T')[0],
        IsPayableOrReceivable: modalType === 'client', IsActive: true,
        LedgerGroupId: null, LedgerGroupName: '', GroupCreditDays: 0, GroupCreditLimit: 0,
        IsType: defaultIsType, InvoiceTypeId: 0, SalesExecutiveId: 0, StateCodeId: null,
        CountryId: null, ISOCode: '', Symbol: '',
        CurrencyTypeId: null, CurrencyName: '',
        AddressLine1: '', AddressLine2: '', Location: '', PinCode: '',
        DeliveryAddressLine1: '', DeliveryAddressLine2: '', DeliveryLocation: '', DeliveryPinCode: '',
        ContactNumber1: '', ContactNumber2: '', ContactNumber3: '', Email: '',
        VATNumber: '', PanNumber: '', MSMENumber: '', CreditDays: 0, CreditLimit: 0,
        IECCode: '', TradeLicenseNo: ''
      });
      setLedgerGroupInputValue('');
    }
  }, [selectedItem, modalAction, modalType, setFormData]);

  // Sync input value when ledger groups are loaded and formData has a value
  useEffect(() => {
    if (ledgerGroups.length > 0 && formData.LedgerGroupId && modalAction !== 'create') {
      const matchedGroup = ledgerGroups.find(g => g.Id === formData.LedgerGroupId);
      if (matchedGroup) {
        setLedgerGroupInputValue(matchedGroup.Name);
      }
    }
  }, [ledgerGroups, formData.LedgerGroupId, modalAction]);

  // Sync country input when countries loaded
  useEffect(() => {
    // country value is controlled directly via formData.CountryId on the select
  }, [countries, formData.CountryId, modalAction]);

  // Ensure CurrencyTypeId and CurrencyName are set when editing
  useEffect(() => {
    if (modalAction !== 'create' && selectedItem && formData.CurrencyTypeId) {
      const matchedCurrency = currencies.find(c => c.Id === formData.CurrencyTypeId);
      if (matchedCurrency && !formData.CurrencyName) {
        setFormData(prev => ({
          ...prev,
          CurrencyName: matchedCurrency.Currency || ''
        }));
      }
    }
  }, [currencies, formData.CurrencyTypeId, modalAction, selectedItem]);

  const fetchLedgerGroups = async () => {
    setLedgerGroupLoading(true);
    try {
      const response = await fetchData('ClientAndVendorApi', `/LedgerGroupList/${companyId}`, token);
      setLedgerGroups(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching ledger groups:', error);
      setLedgerGroups([]);
    }
    setLedgerGroupLoading(false);
  };

  const fetchBalanceSheetHeads = async () => {
    setBalanceSheetLoading(true);
    try {
      const response = await fetchData('ClientAndVendorApi', `/BalanceSheetList/${companyId}`, token);
      setBalanceSheetHeads(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching balance sheet heads:', error);
      setBalanceSheetHeads([]);
    }
    setBalanceSheetLoading(false);
  };

  const fetchCountries = async () => {
    setCountryLoading(true);
    try {
      const response = await fetchData('CountryApi', `/${companyId}`, token);
      setCountries(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
    setCountryLoading(false);
  };

  const fetchCurrencies = async () => {
    setCurrencyLoading(true);
    try {
      const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
      setCurrencies(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setCurrencies([]);
    }
    setCurrencyLoading(false);
  };

  const handleCreateLedgerGroup = async (name) => {
    if (!name?.trim()) return null;
    setCreatingNewGroup(true);
    try {
      const newGroup = {
        CompanyId: companyId, Name: name.trim(),
        CreditDays: formData.GroupCreditDays || 0,
        CreditLimit: formData.GroupCreditLimit || 0, IsActive: true
      };
      const response = await postData('ClientAndVendorApi', '/LedgerGroup', newGroup, token);
      const createdGroup = response.response_data || response;
      await fetchLedgerGroups();
      return createdGroup;
    } catch (error) {
      console.error('Error creating ledger group:', error);
      return null;
    } finally {
      setCreatingNewGroup(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-sync SearchableName with Name
      if (field === 'Name') {
        updated.SearchableName = value;
      }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLedgerGroupChange = async (_, newValue) => {
    if (typeof newValue === 'string') {
      const createdGroup = await handleCreateLedgerGroup(newValue);
      if (createdGroup) {
        handleInputChange('LedgerGroupId', createdGroup.Id);
        handleInputChange('LedgerGroupName', createdGroup.Name);
        setLedgerGroupInputValue(createdGroup.Name);
      }
    } else if (newValue?.inputValue) {
      const createdGroup = await handleCreateLedgerGroup(newValue.inputValue);
      if (createdGroup) {
        handleInputChange('LedgerGroupId', createdGroup.Id);
        handleInputChange('LedgerGroupName', createdGroup.Name);
        setLedgerGroupInputValue(createdGroup.Name);
      }
    } else if (newValue) {
      handleInputChange('LedgerGroupId', newValue.Id);
      handleInputChange('LedgerGroupName', newValue.Name);
      setLedgerGroupInputValue(newValue.Name);
    } else {
      handleInputChange('LedgerGroupId', null);
      handleInputChange('LedgerGroupName', '');
      setLedgerGroupInputValue('');
    }
  };

  const handleBothToggle = (checked) => {
    handleInputChange('IsType', checked ? 3 : (modalType === 'client' ? 1 : 2));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!(formData.Name || formData.name)?.toString().trim()) newErrors.Name = 'Name is required';
    if (!formData.BalanceSheetHeadId) newErrors.BalanceSheetHeadId = 'Balance sheet head is required';
    if (!formData.ContactNumber1?.toString().trim()) newErrors.ContactNumber1 = 'Contact number is required';
    if (!formData.AddressLine1?.toString().trim()) newErrors.AddressLine1 = 'Address is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => { if (validateForm()) onSave(); };

  const getModalTitle = () => {
    const typeNames = { client: 'Customer', vendor: 'Supplier' };
    const actionNames = { create: 'New', edit: 'Edit', view: 'View' };
    const typeName = typeNames[modalType] || 'Master';
    return {
      title: `${actionNames[modalAction] || 'New'} ${typeName}`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} ${typeName.toLowerCase()} details`
    };
  };

  const filterOptions = (options, params) => {
    const filtered = options.filter(opt => opt.Name?.toLowerCase().includes(params.inputValue.toLowerCase()));
    if (params.inputValue !== '' && !filtered.some(opt => opt.Name?.toLowerCase() === params.inputValue.toLowerCase())) {
      filtered.push({ inputValue: params.inputValue, Name: `Add "${params.inputValue}"` });
    }
    return filtered;
  };

  const isDisabled = modalAction === 'view';

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      className="item-modal"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px'
        }
      }}
    >
      <DialogTitle>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h6>{getModalTitle().title}</h6>
          </div>
          <Button onClick={onClose} className="modal-close-btn"><Close /></Button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, pt: 0.25 }}>
        {/* Basic Information */}
        <Box sx={{ ...sectionStyle, mt: 0, mb: 0.5 }}>Basic Information</Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Name *" value={formData.Name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)} error={!!errors.Name}
            helperText={errors.Name} disabled={isDisabled} sx={fieldStyle} />

          <TextField size="small" label="Searchable Name" value={formData.SearchableName || ''}
            onChange={(e) => handleInputChange('SearchableName', e.target.value)}
            disabled={isDisabled} sx={fieldStyle} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 0.8fr', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <Autocomplete
            size="small"
            value={balanceSheetHeads.find(b => b.Id === formData.BalanceSheetHeadId) || null}
            onChange={(_, v) => handleInputChange('BalanceSheetHeadId', v?.Id || null)}
            options={balanceSheetHeads}
            getOptionLabel={(opt) => opt.Name || ''}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                minHeight: "33px",
                height: "33px",
                fontSize: "11px",
              },
              "& .MuiAutocomplete-input": {
                padding: "2px 4px !important",
              },
              "& .MuiInputLabel-root": {
                fontSize: "11px",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "calc(50% - 8px)",
              },
            }}
            ListboxProps={{
              style: {
                fontSize: "11px",
                maxHeight: "200px",
              },
            }}
            loading={balanceSheetLoading}
            disabled={isDisabled}
            slotProps={{
              popper: {
                sx: autocompleteListStyle
              }
            }}
            renderInput={(params) => <TextField {...params} label="Balance Sheet Head *"
              error={!!errors.BalanceSheetHeadId} helperText={errors.BalanceSheetHeadId}
              InputProps={{
                ...params.InputProps, endAdornment: <>{balanceSheetLoading
                  && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>
              }} />}
          />

          <TextField size="small" label="Opening Balance (AED)" type="number" value={formData.OpeningBalance ?? 0}
            onChange={(e) => handleInputChange('OpeningBalance', parseFloat(e.target.value) || 0)}
            disabled={isDisabled} sx={fieldStyle} />

          <TextField size="small" label="Opening Date" type="date" value={formData.OpeningDate?.split('T')[0] || ''}
            onChange={(e) => handleInputChange('OpeningDate', e.target.value)}
            disabled={isDisabled} InputLabelProps={{ shrink: true }} sx={fieldStyle} />

          <FormControlLabel control={<Switch size="small" checked={formData.IsPayableOrReceivable ?? false}
            onChange={(e) => handleInputChange('IsPayableOrReceivable', e.target.checked)}
            disabled={isDisabled} />} label={modalType === 'client' ? 'Receivable' : 'Payable'} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' }, mt: 0.5 }} />
        </Box>

        {/* Ledger Group */}
        <Box sx={sectionStyle}>Ledger Group</Box>
        <Box sx={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          gap: 1, mb: 1, alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <Autocomplete
            size="small"
            value={ledgerGroups.find(g => g.Id === formData.LedgerGroupId) || null}
            onChange={handleLedgerGroupChange}
            inputValue={ledgerGroupInputValue}
            onInputChange={(_, v, r) => { if (r !== 'reset') { setLedgerGroupInputValue(v); } }}
            onBlur={() => {
              const sel = ledgerGroups.find(g => g.Id === formData.LedgerGroupId);
              if (sel) { setLedgerGroupInputValue(sel.Name || ''); handleInputChange('LedgerGroupName', sel.Name || ''); }
              else if (ledgerGroupInputValue) handleInputChange('LedgerGroupName', ledgerGroupInputValue);
            }}
            filterOptions={filterOptions}
            options={ledgerGroups}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.inputValue || opt.Name || ''}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                minHeight: "33px",
                height: "33px",
                fontSize: "11px",
              },
              "& .MuiAutocomplete-input": {
                padding: "2px 4px !important",
              },
              "& .MuiInputLabel-root": {
                fontSize: "11px",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "calc(50% - 8px)",
              },
            }}
            ListboxProps={{
              style: {
                fontSize: "11px",
                maxHeight: "200px",
              },
            }}

            renderOption={(props, opt) => <li {...props}>{opt.inputValue ?
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#1e3a8a' }}>
                <Add fontSize="small" /> {opt.Name}</span> : opt.Name}</li>}
            freeSolo selectOnFocus handleHomeEndKeys
            loading={ledgerGroupLoading || creatingNewGroup}
            disabled={isDisabled}
            slotProps={{
              popper: {
                sx: autocompleteListStyle
              }
            }}
            renderInput={(params) => <TextField {...params} label="Ledger Group (Select or Create)"
              InputProps={{
                ...params.InputProps, endAdornment: <>{(ledgerGroupLoading || creatingNewGroup)
                  && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>
              }} />}
          />
          {!isDisabled && (
            <FormControlLabel control={<Switch size="small" checked={formData.IsType === 3} onChange={(e) => handleBothToggle(e.target.checked)} />}
              label={`Also mark as ${modalType === 'client' ? 'Supplier' : 'Customer'} (Both)`}
              sx={{ whiteSpace: isMobile ? 'normal' : 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }} />
          )}
        </Box>

        {/* Type section removed as it's now inline */}

        {/* Contact Information */}
        <Box sx={sectionStyle}>Contact Information</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Contact Number 1 *" value={formData.ContactNumber1 || ''} onChange={(e) => handleInputChange('ContactNumber1', e.target.value)} error={!!errors.ContactNumber1} helperText={errors.ContactNumber1} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Contact Number 2" value={formData.ContactNumber2 || ''} onChange={(e) => handleInputChange('ContactNumber2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Contact Number 3" value={formData.ContactNumber3 || ''} onChange={(e) => handleInputChange('ContactNumber3', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>
        <TextField size="small" fullWidth label="Email" type="email" value={formData.Email || ''} onChange={(e) => handleInputChange('Email', e.target.value)} disabled={isDisabled} sx={{ mb: 1, ...fieldStyle }} />

        {/* Address */}
        <Box sx={sectionStyle}>Address</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 70px 1fr 1fr 80px', gap: 0.5, mb: 1 }}>
          <Autocomplete
            size="small"
            value={countries.find(c => c.Id === formData.CountryId) || null}
            onChange={(_, v) => {
              setFormData(prev => ({ ...prev, CountryId: v?.Id || null, ISOCode: v?.ISOCode || '' }));
            }}
            options={countries}
            getOptionLabel={(opt) => opt.CountryName || opt.Name || ''}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                minHeight: "33px",
                height: "33px",
                fontSize: "11px",
              },
              "& .MuiAutocomplete-input": {
                padding: "2px 4px !important",
              },
              "& .MuiInputLabel-root": {
                fontSize: "11px",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "calc(50% - 8px)",
              },
            }}
            ListboxProps={{
              style: {
                fontSize: "11px",
                maxHeight: "200px",
              },
            }}

            loading={countryLoading}
            disabled={isDisabled}
            slotProps={{ popper: { sx: autocompleteListStyle } }}
            renderInput={(params) => (
              <TextField {...params} label="Country" size="small"
                InputProps={{ ...params.InputProps, endAdornment: <>{countryLoading && <CircularProgress size={14} />}{params.InputProps.endAdornment}</> }}
              />
            )}
          />

          <TextField size="small" label="ISO" value={formData.ISOCode || ''} disabled
            sx={{ ...fieldStyle, '& .MuiOutlinedInput-root': { ...fieldStyle['& .MuiOutlinedInput-root'], background: '#f5f5f5' } }}
          />

          <Autocomplete
            size="small"
            value={currencies.find(c => c.Id === formData.CurrencyTypeId) || null}
            options={currencies}
            loading={currencyLoading}
            disabled={isDisabled}
            onChange={(_, v) => {
              setFormData(prev => ({
                ...prev,
                CurrencyTypeId: v?.Id || null,
                CurrencyName: v?.Currency || ""
              }));
            }}
            getOptionLabel={(opt) => opt.Currency || ""}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                minHeight: "33px",
                height: "33px",
                fontSize: "11px",
              },
              "& .MuiAutocomplete-input": {
                padding: "2px 4px !important",
              },
              "& .MuiInputLabel-root": {
                fontSize: "11px",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "calc(50% - 8px)",
              },
            }}
            ListboxProps={{
              style: {
                fontSize: "11px",
                maxHeight: "200px",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Currency"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField size="small" label="Location" value={formData.Location || ''} onChange={(e) => handleInputChange('Location', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Pin" value={formData.PinCode || ''} onChange={(e) => handleInputChange('PinCode', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0.5, mb: 1 }}>
          <TextField size="small" label="Address Line 1 *" value={formData.AddressLine1 || ''} onChange={(e) => handleInputChange('AddressLine1', e.target.value)} error={!!errors.AddressLine1} helperText={errors.AddressLine1} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Address Line 2" value={formData.AddressLine2 || ''} onChange={(e) => handleInputChange('AddressLine2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>

        {/* Delivery Address */}
        <Box sx={sectionStyle}>Delivery Address</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Delivery Address Line 1" value={formData.DeliveryAddressLine1 || ''} onChange={(e) => handleInputChange('DeliveryAddressLine1', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Delivery Location" value={formData.DeliveryLocation || ''} onChange={(e) => handleInputChange('DeliveryLocation', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Delivery Address Line 2" value={formData.DeliveryAddressLine2 || ''} onChange={(e) => handleInputChange('DeliveryAddressLine2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Delivery Pin Code" value={formData.DeliveryPinCode || ''} onChange={(e) => handleInputChange('DeliveryPinCode', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>

        {/* Tax & Credit */}
        <Box sx={sectionStyle}>Tax & Credit Information</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="VAT Number" value={formData.VATNumber || ''} onChange={(e) => handleInputChange('VATNumber', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Credit Limit (₹)" type="number" value={formData.CreditLimit ?? 0} onChange={(e) => handleInputChange('CreditLimit', parseFloat(e.target.value) || 0)} disabled={isDisabled} inputProps={{ min: 0 }} sx={fieldStyle} />
          <TextField size="small" label="Credit Days" type="number" value={formData.CreditDays ?? 0} onChange={(e) => handleInputChange('CreditDays', parseInt(e.target.value) || 0)} disabled={isDisabled} inputProps={{ min: 0 }} sx={fieldStyle} />
        </Box>
      
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1 }}>
          <TextField size="small" label="IEC Code" value={formData.IECCode || ''} onChange={(e) => handleInputChange('IECCode', e.target.value)} disabled={isDisabled} sx={fieldStyle} inputProps={{ maxLength: 50 }} />
          <TextField size="small" label="Trade License No" value={formData.TradeLicenseNo || ''} onChange={(e) => handleInputChange('TradeLicenseNo', e.target.value)} disabled={isDisabled} sx={fieldStyle} inputProps={{ maxLength: 50 }} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 1.5, py: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ fontSize: '0.7rem', padding: '4px 12px' }}>Cancel</Button>
        {!isDisabled && (
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={loading || creatingNewGroup}
            sx={{ fontSize: '0.7rem', padding: '4px 12px', minWidth: '100px' }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Saving...' : modalAction === 'create' ? `Add ${modalType === 'client' ? 'Customer' : 'Supplier'}` : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MasterModal;
